// src/pages/MusicPage.js
import React from 'react';
import { Box, Container, Typography, Grid, Divider } from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TextProvider } from '../context/TextContext';
import medalImage from '../assets/medal.jpg';
import guitarImage from '../assets/guitar.jpg';
import bassGuitarImage from '../assets/bass-guitar.jpg';
import pianoImage from '../assets/piano.jpg';
import tambouraImage from '../assets/Macedonian-tambura.png';

const MusicPage = () => {
  const instruments = [
    { name: 'Guitar', image: guitarImage },
    { name: 'Bass Guitar', image: bassGuitarImage },
    { name: 'Piano', image: pianoImage },
    { name: 'Tamboura', image: tambouraImage }
  ];

  return (
    <TextProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F3F2F1' }}>
        <Header />
        <Container sx={{ flex: 1, paddingY: { xs: 4, md: 6 }, maxWidth: '900px' }}>
          {/* Page Title */}
          <Typography 
            variant="h3" 
            gutterBottom 
            sx={{ 
              fontWeight: 300,
              letterSpacing: '-0.02em',
              marginBottom: 3,
              color: '#1a1a1a'
            }}
          >
            Beyond Work
          </Typography>

          <Divider sx={{ marginY: 4, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

          {/* Introduction */}
          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: '#4a4a4a',
              marginBottom: 4,
              fontWeight: 300
            }}
          >
            Music has been a parallel journey alongside my engineering career for over fifteen years. 
            What began as a personal interest has evolved into a disciplined practice that complements 
            my professional work, offering a different lens through which to approach creativity, focus, 
            and problem-solving.
          </Typography>

          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: '#4a4a4a',
              marginBottom: 4,
              fontWeight: 300
            }}
          >
            I have not taken formal music classes. Instead, I learned through dedicated practice, 
            careful listening, and long-term commitment. This self-directed approach has reinforced 
            the value of consistent practice, the importance of understanding fundamentals, and the 
            patience required to develop complex skills over time. These principles translate directly 
            to software engineering, where building robust systems requires similar discipline and 
            long-term thinking.
          </Typography>

          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: '#4a4a4a',
              marginBottom: 6,
              fontWeight: 300
            }}
          >
            Over the years, music has also provided opportunities to travel around the world, 
            performing in different countries and experiencing diverse musical traditions. These 
            experiences have broadened my perspective and enriched both my musical understanding 
            and my approach to collaborative work.
          </Typography>

          <Divider sx={{ marginY: 4, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

          {/* Influence Section */}
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 400,
              marginBottom: 3,
              color: '#1a1a1a',
              letterSpacing: '-0.01em'
            }}
          >
            Creative Influence
          </Typography>

          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: '#4a4a4a',
              marginBottom: 3,
              fontWeight: 300
            }}
          >
            The practice of music has influenced my approach to software engineering in several ways. 
            The iterative process of learning a piece, breaking it down into smaller components, and 
            gradually building proficiency mirrors the way I approach complex technical problems.
          </Typography>

          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: '#4a4a4a',
              marginBottom: 6,
              fontWeight: 300
            }}
          >
            Music requires both analytical thinking and intuitive expression. Similarly, engineering 
            demands logical problem-solving alongside creative solutions. The discipline of regular 
            practice has reinforced the importance of consistency in professional development, while 
            the collaborative nature of ensemble playing has enhanced my ability to work effectively 
            within technical teams.
          </Typography>

          <Divider sx={{ marginY: 4, borderColor: 'rgba(0, 0, 0, 0.08)' }} />

          {/* Gallery Section */}
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 400,
              marginBottom: 3,
              color: '#1a1a1a',
              letterSpacing: '-0.01em'
            }}
          >
            Gallery
          </Typography>

          <Grid container spacing={3} sx={{ marginBottom: 6 }}>
            {/* Instruments */}
            {instruments.map((instrument, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    backgroundColor: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '200px',
                      backgroundColor: '#FAF9F8',
                      border: '1px solid #EDEBE9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    <Box
                      component="img"
                      src={instrument.image}
                      alt={instrument.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                      sx={{
                        width: (instrument.name === 'Guitar' || instrument.name === 'Bass Guitar') 
                          ? '200px' 
                          : '100%',
                        // height: (instrument.name === 'Guitar' || instrument.name === 'Bass Guitar') 
                        //   ? '100%' 
                        //   : '200px',
                        objectFit: 'cover',
                        transform: (instrument.name === 'Guitar' || instrument.name === 'Bass Guitar') 
                          ? 'rotate(90deg)' 
                          : 'none',
                        transformOrigin: 'center center'
                      }}
                    />
                  </Box>
                  <Box sx={{ padding: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 400,
                        color: '#1a1a1a'
                      }}
                    >
                      {instrument.name}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
            
            {/* Medal */}
            <Grid item xs={12} sm={6} md={4}>
              <Box
                sx={{
                  backgroundColor: 'white',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}
              >
                <Box
                  component="img"
                  src={medalImage}
                  alt="First Place Guitar Medal - Albena, Bulgaria"
                  sx={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
                <Box sx={{ padding: 3 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 400,
                      marginBottom: 1,
                      color: '#1a1a1a'
                    }}
                  >
                    International Competition
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#6a6a6a',
                      fontSize: '0.95rem',
                      lineHeight: 1.6
                    }}
                  >
                    First Place - Guitar<br />
                    Albena, Bulgaria
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
        <Footer />
      </Box>
    </TextProvider>
  );
};

export default MusicPage;

